import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Smartphone, 
  Briefcase, 
  Users, 
  TrendingUp, 
  Coins, 
  LogOut,
  Menu,
  X,
  Inbox,
  GraduationCap,
  Mail,
  ShieldCheck,
  Sparkles
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import Logo from '../../assets/quivix.png';
import '../admin.css';

const AdminLayout = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
    { name: 'Apps Catalog', icon: Smartphone, path: '/admin/apps' },
    { name: 'Job Openings', icon: Briefcase, path: '/admin/jobs' },
    { name: 'Internships', icon: GraduationCap, path: '/admin/internships' },
    { name: 'Applications', icon: Inbox, path: '/admin/applications' },
    { name: 'Team Members', icon: Users, path: '/admin/team' },
    { name: 'Revenue & AdMob', icon: TrendingUp, path: '/admin/earnings' },
    { name: 'Push Broadcast', icon: Mail, path: '/admin/push-email' },
    { name: 'Investments', icon: Coins, path: '/admin/investment' },
  ];

  const getPageTitle = () => {
    const item = navItems.find(item => item.path === location.pathname);
    return item ? item.name : 'Central Command';
  };

  return (
    <div className="flex min-h-screen admin-theme text-slate-200 overflow-hidden font-sans selection:bg-indigo-500/30">
      {/* Ambient Glow Background Orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-indigo-600/15 blur-[140px] rounded-full" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-600/15 blur-[140px] rounded-full" />
      </div>

      {/* Side Drawer Navigation */}
      <motion.aside 
        initial={false}
        animate={{ width: isSidebarOpen ? 280 : 84 }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="glass-panel sticky top-0 h-screen z-50 flex flex-col border-r border-white/10 shrink-0 bg-[#070b14]/80 backdrop-blur-xl"
      >
        {/* Header Logo */}
        <div className="p-5 flex items-center gap-3.5 border-b border-white/10 relative overflow-hidden">
          <div className="w-11 h-11 rounded-2xl bg-neutral-900 border border-white/20 p-2 flex items-center justify-center shrink-0 shadow-lg shadow-indigo-500/20 group hover:scale-105 transition-transform">
            <img src={Logo} alt="Quivix Core" className="w-full h-full object-contain" />
          </div>

          <AnimatePresence>
            {isSidebarOpen && (
              <motion.div 
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="flex flex-col min-w-0"
              >
                <span className="font-heading font-black text-lg text-white tracking-tight leading-none flex items-center gap-1.5">
                  QUIVIX <span className="text-gradient">CORE</span>
                </span>
                <span className="text-[9px] font-mono text-indigo-400 uppercase tracking-widest mt-1">
                  Control Center v3.0
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 px-3 py-6 space-y-1.5 overflow-y-auto overflow-x-hidden">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || (item.path === '/admin' && location.pathname === '/admin');

            return (
              <NavLink 
                key={item.name} 
                to={item.path}
                end={item.path === '/admin'}
                className={({ isActive }) => `
                  group relative flex items-center gap-3.5 px-4 py-3 rounded-2xl text-xs font-medium transition-all duration-300
                  ${isActive 
                    ? 'bg-gradient-to-r from-indigo-600/20 to-purple-600/10 text-white font-bold border border-indigo-500/30 shadow-lg shadow-indigo-500/10' 
                    : 'text-slate-400 hover:text-white hover:bg-white/[0.04] border border-transparent'}
                `}
              >
                <item.icon size={20} className={`shrink-0 transition-colors ${isActive ? 'text-indigo-400' : 'text-slate-400 group-hover:text-white'}`} />
                
                {isSidebarOpen && (
                  <motion.span 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="truncate flex-1 text-xs"
                  >
                    {item.name}
                  </motion.span>
                )}

                {isActive && (
                  <motion.div 
                    layoutId="active-indicator" 
                    className="w-1.5 h-1.5 rounded-full bg-indigo-400 shadow-[0_0_10px_#818cf8] shrink-0" 
                  />
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* User Profile / System Status Footer */}
        <div className="p-4 border-t border-white/10 bg-black/40">
          {isSidebarOpen ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3 px-1">
                <div className="w-10 h-10 rounded-2xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 font-bold shrink-0 shadow-md">
                  {user?.username?.[0]?.toUpperCase() || 'S'}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-white truncate font-heading">{user?.username || 'Salman Khan'}</p>
                  <span className="inline-flex items-center gap-1 text-[9px] font-mono text-emerald-400 uppercase tracking-wider mt-0.5">
                    <ShieldCheck className="w-3 h-3" /> Master Admin
                  </span>
                </div>
              </div>

              <button 
                onClick={handleLogout}
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-white/5 hover:bg-red-500/15 text-slate-300 hover:text-red-400 border border-white/10 hover:border-red-500/20 text-xs font-semibold transition-all group active:scale-95"
              >
                <LogOut size={15} className="group-hover:-translate-x-0.5 transition-transform" />
                <span>Sign Out</span>
              </button>
            </div>
          ) : (
            <button 
              onClick={() => setIsSidebarOpen(true)}
              className="w-full py-2 flex justify-center text-indigo-400 hover:text-white transition-colors"
            >
              <Menu size={22} />
            </button>
          )}
        </div>
      </motion.aside>

      {/* Main Container Area */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden relative z-10">
        
        {/* Top Floating Glass Header */}
        <header className="h-20 glass-panel flex items-center justify-between px-6 sm:px-8 border-b border-white/10 z-40 bg-[#070b14]/70 backdrop-blur-xl">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-300 hover:text-white transition-colors border border-white/10"
            >
              {isSidebarOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            
            <div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-indigo-400 block">
                Quivix Administration
              </span>
              <h2 className="admin-heading text-lg font-bold tracking-tight text-white font-heading mt-0.5 flex items-center gap-2">
                {getPageTitle()} <Sparkles size={14} className="text-indigo-400" />
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-mono">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span>Core Operational</span>
            </div>

            <div className="flex items-center gap-3 pl-4 border-l border-white/10">
              <div className="w-9 h-9 rounded-full bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center text-indigo-300 text-xs font-bold font-mono">
                {user?.username?.[0]?.toUpperCase() || 'S'}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-xs font-bold text-white font-heading">{user?.username || 'Salman Khan'}</p>
                <p className="text-[9px] font-mono text-slate-400 uppercase tracking-widest">New Mexico, USA</p>
              </div>
            </div>
          </div>
        </header>

        {/* Content Outlet */}
        <main className="flex-1 overflow-y-auto p-6 sm:p-8 relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
